import { IcInfo16 } from '@assets/icons/16x16';
import { createListCollection, Field as ChakraField, Fieldset, Icon } from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import ModifiedTag from '@components/common/form/ModifiedField';
import MultiSelect from '@components/ui/multiSelect/MultiSelect';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole } from '@redux/authent/authent.selectors';
import { PilotSelectors, StrategicDroneSelectors } from '@redux/utm/utm.selectors';
import { haveSameUniqueElements } from '@utils/common.utils';
import { getPilotName } from '@utils/utm/pilot.utils';
import { useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { NormalizedFleet } from '@/types/utm/fleet.type';

export default function DronesAndPilotsTab() {
  const { formatMessage } = useIntl();
  const pilots = useAppSelector(PilotSelectors.selectAllPilots);
  const drones = useAppSelector(StrategicDroneSelectors.selectAllStrategicDrones);
  const canSeeDrones = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_DRONE));
  const canSeePilots = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_PILOT));
  const { errors, touched, initialValues, values, setFieldValue } = useFormikContext<NormalizedFleet>();

  return (
    <CockpitFormLayout title="components.utm.fleets.tab.dronesAndPilots.title">
      <Fieldset.Root invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}>
        {canSeeDrones && (
          <ChakraField.Root variant="cockpit">
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.utm.fleets.tab.dronesAndPilots.drones" />
              <Tooltip
                content={formatMessage({ id: 'components.utm.fleets.tab.dronesAndPilots.droneHelp' })}
                positioning={{ placement: 'right' }}
                closeDelay={0}
              >
                <Icon as={IcInfo16} boxSize="16px" color="neutral.500" _hover={{ color: 'skyBlue.500' }} />
              </Tooltip>
              {!haveSameUniqueElements(values.drones, initialValues.drones) && <ModifiedTag />}
            </ChakraField.Label>
            <MultiSelect
              values={values.drones}
              label="components.utm.fleets.tab.dronesAndPilots.addDrone"
              collection={createListCollection({
                items: drones.map((drone) => ({
                  label: drone.name,
                  value: drone.id.toString(),
                })),
              })}
              onChange={(values) => setFieldValue('drones', values)}
            />
          </ChakraField.Root>
        )}
        {canSeePilots && (
          <ChakraField.Root variant="cockpit">
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.utm.fleets.tab.dronesAndPilots.pilots" />
              {!haveSameUniqueElements(values.pilots, initialValues.pilots) && <ModifiedTag />}
            </ChakraField.Label>
            <MultiSelect
              values={values.pilots}
              label="components.utm.fleets.tab.dronesAndPilots.addPilot"
              collection={createListCollection({
                items: pilots.map((pilot) => ({
                  label: getPilotName(pilot),
                  value: pilot.code,
                })),
              })}
              onChange={(values) => setFieldValue('pilots', values)}
            />
          </ChakraField.Root>
        )}
      </Fieldset.Root>
    </CockpitFormLayout>
  );
}
